/**
 * Sprint 8 — Physiological UI Layer
 *
 * La interfaz se adapta al estado físico del usuario. `vital_load` (0-100)
 * modula tokens CSS que viven en :root y son consumidos por animaciones,
 * densidad visual y opacidad de texto.
 *
 * Sprint 8.1 Fix 2 — interpolación suave (lerp) en requestAnimationFrame,
 * para que el cambio entre perfiles físicos NO sea un flip brusco sino una
 * transición percibida como natural.
 */

const PROFILES = {
  exhausted: {
    motion: 1.5,
    density: 0.85,
    mutedOpacity: 0.45,
    pulseSpeed: '4s',
    accentIntensity: 0.7
  },
  normal: {
    motion: 1.15,
    density: 0.95,
    mutedOpacity: 0.50,
    pulseSpeed: '3s',
    accentIntensity: 0.9
  },
  fresh: {
    motion: 1.0,
    density: 1.0,
    mutedOpacity: 0.55,
    pulseSpeed: '2.5s',
    accentIntensity: 1.0
  }
}

function profileForVitalLoad(vitalLoad) {
  if (typeof vitalLoad !== 'number') return PROFILES.normal
  if (vitalLoad < 40) return PROFILES.exhausted
  if (vitalLoad > 75) return PROFILES.fresh
  return PROFILES.normal
}

// Estado actual interpolado (módulo-level para que las llamadas sucesivas
// continúen desde donde están, no salten al target instantáneamente).
const current = {
  motion: 1.15,
  density: 0.95,
  mutedOpacity: 0.50,
  accentIntensity: 0.9
}
let rafId = null
let target = { ...current }
let pulseSpeed = '3s'

function lerp(a, b, t) {
  return a + (b - a) * t
}

function frame() {
  let stillAnimating = false
  const factor = 0.08 // ~12 frames a 60fps para convergencia visual

  for (const key of Object.keys(current)) {
    const diff = target[key] - current[key]
    if (Math.abs(diff) < 0.0008) {
      current[key] = target[key]
    } else {
      current[key] = lerp(current[key], target[key], factor)
      stillAnimating = true
    }
  }

  if (typeof document !== 'undefined') {
    const root = document.documentElement
    root.style.setProperty('--motion-duration-multiplier', current.motion.toFixed(3))
    root.style.setProperty('--visual-density', current.density.toFixed(3))
    root.style.setProperty('--text-muted-opacity', current.mutedOpacity.toFixed(3))
    root.style.setProperty('--accent-intensity', current.accentIntensity.toFixed(3))
    // pulseSpeed se aplica directo (no se lerp; es una unidad de tiempo)
    root.style.setProperty('--auria-pulse-speed', pulseSpeed)
  }

  if (stillAnimating) {
    rafId = requestAnimationFrame(frame)
  } else {
    rafId = null
  }
}

function applyPhysiologicalTokens(vitalLoad) {
  if (typeof document === 'undefined') return
  const p = profileForVitalLoad(vitalLoad)

  target = {
    motion: p.motion,
    density: p.density,
    mutedOpacity: p.mutedOpacity,
    accentIntensity: p.accentIntensity
  }
  pulseSpeed = p.pulseSpeed

  if (rafId == null) rafId = requestAnimationFrame(frame)
}

function describePhysiologicalState(vitalLoad) {
  if (typeof vitalLoad !== 'number') return 'normal'
  if (vitalLoad < 40) return 'exhausted'
  if (vitalLoad > 75) return 'fresh'
  return 'normal'
}

module.exports = { applyPhysiologicalTokens, describePhysiologicalState }
